import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';

// Main Card Component
function AddressCard({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'bg-card rounded-lg border border-gray-200 p-4 transition-colors hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600',
        className
      )}
      {...props}
    />
  );
}

// Header Container (for Title and Dropdown)
function AddressCardHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-start justify-between mb-3', className)}
      {...props}
    />
  );
}

// Title Component
function AddressCardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return (
    <h3
      className={cn(
        'text-lg font-semibold text-gray-900 dark:text-white',
        className
      )}
      {...props}
    />
  );
}

// Ellipsis Dropdown Component
function AddressCardEllipsisDropdown({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center justify-center rounded-md p-1 transition-colors hover:bg-gray-100 focus:outline-none',
            className
          )}
        >
          <MoreVertical className="h-5 w-5 text-gray-600" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}

// Ellipsis Dropdown Item Component
function AddressCardEllipsisDropdownItem({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof DropdownMenuItem> & {
  variant?: 'default' | 'destructive';
}) {
  return (
    <DropdownMenuItem className={cn(className)} variant={variant} {...props} />
  );
}

// Detail Container Component
function AddressCardDetail({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'space-y-1 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700',
        className
      )}
      {...props}
    />
  );
}

// Detail Item Components
function AddressCardDetailFullAddress({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'text-sm text-gray-700 font-medium dark:text-gray-300',
        className
      )}
      {...props}
    />
  );
}

function AddressCardDetailCity({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-sm text-gray-600 dark:text-gray-400', className)}
      {...props}
    />
  );
}

function AddressCardDetailZipCode({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-sm text-gray-600 dark:text-gray-400', className)}
      {...props}
    />
  );
}

function AddressCardDetailDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'text-sm text-gray-600 dark:text-gray-400 italic',
        className
      )}
      {...props}
    />
  );
}

// Additional Info Container Component
function AddressCardAdditionalInfo({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return <div className={cn('flex flex-wrap gap-2', className)} {...props} />;
}

// Additional Info Item Component
function AddressCardAdditionalInfoItem({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 rounded-md border border-gray-200  bg-gray-50 text-xs font-medium text-gray-700',
        className
      )}
      {...props}
    />
  );
}

// Actions Container Component
function AddressCardActions({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700',
        className
      )}
      {...props}
    />
  );
}

// Compound component pattern
AddressCard.Header = AddressCardHeader;
AddressCard.Title = AddressCardTitle;
AddressCard.EllipsisDropdown = Object.assign(AddressCardEllipsisDropdown, {
  Item: AddressCardEllipsisDropdownItem,
});
AddressCard.Detail = Object.assign(AddressCardDetail, {
  FullAddress: AddressCardDetailFullAddress,
  City: AddressCardDetailCity,
  ZipCode: AddressCardDetailZipCode,
  Description: AddressCardDetailDescription,
});
AddressCard.AdditionalInfo = Object.assign(AddressCardAdditionalInfo, {
  Item: AddressCardAdditionalInfoItem,
});
AddressCard.Actions = AddressCardActions;

export { AddressCard };
